import { Observable, from, of } from 'rxjs';

import { HttpRequest, HttpResponse } from '@angular/common/http';

import { createMockCloudinaryImageRow } from '@app/mock-api/mock-api-property-media-shape';
import { ensureMockApiPropertyStoreSeeded, mockApiCities, mockApiDistrictsByCity, mockApiProperties } from '@app/mock-api/mock-api-property-store';
import type { MockCity, MockDistrict, MockImageRow, MockPropertyRecord } from '@app/mock-api/mock-api-shared-models';

export type AdminMockRequestHandler = {
  handle: (req: HttpRequest<unknown>, pathMatch: RegExpMatchArray | null) => Observable<HttpResponse<unknown>>;
  method: string;
  path: RegExp | string;
};

function slugifyDistrictLabel(name: string): string {
  const slug = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return slug.length > 0 ? slug : 'bairro';
}

function slugifyCity(name: string, state: string): string {
  const n = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  return `${n}-${state.toLowerCase()}`;
}

function ok<T>(data: T) {
  return { errors: [] as { code: string; messageKey: string }[], data, success: true };
}

function envelopeFail(status: number, messageKey: string, code = 'Validation.Failed') {
  return new HttpResponse({
    body: { data: null, errors: [{ code, messageKey }], success: false },
    status,
  });
}

function refCode(): string {
  const n = Math.floor(100_000 + Math.random() * 900_000);
  return `REF-${String(n)}`;
}

function cityOf(id: string): MockCity | undefined {
  return mockApiCities.find((c) => c.id === id);
}

function districtName(cityId: string, districtId: string): string | null {
  return mockApiDistrictsByCity[cityId]?.find((d) => d.id === districtId)?.name ?? null;
}

function primaryUrl(images: MockImageRow[]): string | null {
  const primaryRow = images.find((i) => i.isPrimary) ?? images[0];
  return primaryRow?.urls.listing ?? primaryRow?.url ?? null;
}

function applyImagePlan(
  existing: MockImageRow[],
  imagePlan: { primarySlotIndex?: null | number; slots?: unknown[] },
  newFiles: Map<string, File>,
  mode: 'create' | 'edit',
): MockImageRow[] | ReturnType<typeof envelopeFail> {
  const slotsRaw = imagePlan?.slots;

  if (!Array.isArray(slotsRaw))
    return envelopeFail(400, 'app.adminProperties.media.imagePlanInvalid');

  const slots = slotsRaw as ({ existingImageId?: string; newFileKey?: string } | undefined)[];

  if (slots.length > 30)
    return envelopeFail(400, 'app.adminProperties.media.tooManyImages');

  if (slots.length === 0)
    return [];

  const pi = imagePlan.primarySlotIndex;

  if (pi == null)
    return envelopeFail(400, 'app.adminProperties.media.primarySlotIndexRequired');

  if (typeof pi !== 'number' || pi < 0 || pi >= slots.length)
    return envelopeFail(400, 'app.adminProperties.media.primarySlotIndexOutOfRange');

  const next: MockImageRow[] = [];
  const existingById = new Map(existing.map((i) => [i.id, i]));
  for (let i = 0; i < slots.length; i += 1) {
    const s = slots[i];

    if (s == null || typeof s !== 'object')
      return envelopeFail(400, 'app.adminProperties.media.imagePlanInvalid');

    const ex = 'existingImageId' in s ? s.existingImageId : undefined;
    const nk = 'newFileKey' in s ? s.newFileKey : undefined;
    const hasEx = typeof ex === 'string' && ex.length > 0;
    const hasNk = typeof nk === 'string' && nk.length > 0;

    if (hasEx && hasNk)
      return envelopeFail(400, 'app.adminProperties.media.slotInvalid');

    if (!hasEx && !hasNk)
      return envelopeFail(400, 'app.adminProperties.media.imagePlanInvalid');

    if (hasEx) {
      if (mode === 'create')
        return envelopeFail(400, 'app.adminProperties.media.existingImageNotAllowedOnCreate');

      const prev = existingById.get(ex);

      if (prev == null)
        return envelopeFail(400, 'app.adminProperties.media.existingImageNotFoundOrNotOwned');

      next.push({
        ...prev,
        isPrimary: i === pi,
        sortOrder: i,
      });
    } else {
      const file = newFiles.get(nk!);

      if (file == null)
        return envelopeFail(400, 'app.adminProperties.media.missingFile');

      if (file.size > 10 * 1024 * 1024)
        return envelopeFail(400, 'app.adminProperties.media.fileTooLarge');

      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

      if (!allowed.includes(file.type))
        return envelopeFail(400, 'app.adminProperties.media.fileTypeNotAllowed');

      const id = globalThis.crypto.randomUUID();
      next.push(createMockCloudinaryImageRow({ id, isPrimary: i === pi, sortOrder: i }));
    }
  }

  return next;
}

type ParsedMultipart =
  | {
      readonly files: Map<string, File>;
      readonly imagePlan: { primarySlotIndex?: null | number; slots?: unknown[] };
      readonly ok: true;
      readonly property: Record<string, unknown>;
    }
  | { readonly messageKey: string; readonly ok: false };

export async function parseMultipart(body: FormData): Promise<ParsedMultipart> {
  const imagePlanParts: FormDataEntryValue[] = [];
  const propertyParts: FormDataEntryValue[] = [];

  for (const [fieldKey, value] of body.entries()) {
    if (fieldKey === 'imagePlan')
      imagePlanParts.push(value);
    else if (fieldKey === 'property')
      propertyParts.push(value);
  }

  if (propertyParts.length !== 1)
    return { messageKey: 'app.adminProperties.media.propertyPartInvalid', ok: false };

  const propPart = propertyParts[0];

  if (!(typeof propPart === 'string' || propPart instanceof Blob))
    return { messageKey: 'app.adminProperties.media.propertyPartInvalid', ok: false };

  let propText: string;

  try {
    propText = typeof propPart === 'string' ? propPart : await propPart.text();
  } catch {
    return { messageKey: 'app.adminProperties.media.propertyPartInvalid', ok: false };
  }

  if (propText.trim().length === 0)
    return { messageKey: 'app.adminProperties.media.propertyPartInvalid', ok: false };

  let property: Record<string, unknown>;

  try {
    property = JSON.parse(propText) as Record<string, unknown>;
  } catch {
    return { messageKey: 'app.adminProperties.media.propertyPartInvalid', ok: false };
  }

  let imagePlan: { primarySlotIndex?: null | number; slots?: unknown[] } = {};

  if (imagePlanParts.length > 1)
    return { messageKey: 'app.adminProperties.media.imagePlanInvalid', ok: false };

  const planPart = imagePlanParts.length === 1 ? imagePlanParts[0] : undefined;

  try {
    if (planPart != null && (typeof planPart === 'string' || planPart instanceof Blob)) {
      const planText = typeof planPart === 'string' ? planPart : await planPart.text();

      if (planText.trim().length > 0)
        imagePlan = JSON.parse(planText) as { primarySlotIndex?: null | number; slots?: unknown[] };
    }
  } catch {
    return { messageKey: 'app.adminProperties.media.imagePlanInvalid', ok: false };
  }

  const files = new Map<string, File>();

  for (const [fieldKey, value] of body.entries()) {
    if (fieldKey === 'property' || fieldKey === 'imagePlan')
      continue;

    if (value instanceof File)
      files.set(fieldKey, value);
  }

  return { files, imagePlan, ok: true, property };
}

ensureMockApiPropertyStoreSeeded();

export const mockApiAdminHandlers: AdminMockRequestHandler[] = [
  {
    method: 'GET',
    path: /^\/api\/admin\/cities$/,
    handle: (_req, _m) =>
      of(
        new HttpResponse({
          body: ok(mockApiCities),
          status: 200,
        }),
      ),
  },
  {
    method: 'GET',
    path: /^\/api\/admin\/cities\/([^/]+)\/districts$/,
    handle: (_req, m) => {
      const cityId = decodeURIComponent(m![1]!);
      const rows = mockApiDistrictsByCity[cityId] ?? [];
      return of(
        new HttpResponse({
          body: ok(rows),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/admin\/cities$/,
    handle: (req) => {
      const body = req.body as { name?: string; state?: string } | null;
      const name = typeof body?.name === 'string' ? body.name.trim() : '';
      const state = typeof body?.state === 'string' ? body.state.trim().toUpperCase() : '';

      if (!name || name.length > 120 || state.length !== 2) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }], success: false },
            status: 400,
          }),
        );
      }

      const id = globalThis.crypto.randomUUID();
      const row: MockCity = { id, name, slug: slugifyCity(name, state), state };
      mockApiCities.push(row);

      return of(
        new HttpResponse({
          body: ok(row),
          status: 201,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/admin\/cities\/([^/]+)$/,
    handle: (_req, m) => {
      const id = decodeURIComponent(m![1]!);
      const row = mockApiCities.find((c) => c.id === id);

      if (row == null) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );
      }

      return of(
        new HttpResponse({
          body: ok(row),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'PUT',
    path: /^\/api\/admin\/cities\/([^/]+)$/,
    handle: (req, m) => {
      const id = decodeURIComponent(m![1]!);
      const row = mockApiCities.find((c) => c.id === id);

      if (row == null) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );
      }

      const body = req.body as { name?: string; state?: string } | null;
      const name = typeof body?.name === 'string' ? body.name.trim() : '';
      const state = typeof body?.state === 'string' ? body.state.trim().toUpperCase() : '';

      if (!name || name.length > 120 || state.length !== 2) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }], success: false },
            status: 400,
          }),
        );
      }

      row.name = name;
      row.state = state;
      row.slug = slugifyCity(name, state);

      return of(
        new HttpResponse({
          body: ok(row),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/admin\/cities\/([^/]+)\/districts$/,
    handle: (req, m) => {
      const cityId = decodeURIComponent(m![1]!);
      const city = mockApiCities.find((c) => c.id === cityId);

      if (city == null) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );
      }

      const body = req.body as { name?: string } | null;
      const name = typeof body?.name === 'string' ? body.name.trim() : '';

      if (!name || name.length > 120) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }], success: false },
            status: 400,
          }),
        );
      }

      const list = mockApiDistrictsByCity[cityId] ?? [];
      const dup = list.some((d) => d.name.toLowerCase() === name.toLowerCase());

      if (dup) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'Conflict', messageKey: 'app.validation' }], success: false },
            status: 409,
          }),
        );
      }

      const row: MockDistrict = {
        cityId,
        id: globalThis.crypto.randomUUID(),
        name,
        slug: slugifyDistrictLabel(name),
      };
      mockApiDistrictsByCity[cityId] = [...list, row];

      return of(
        new HttpResponse({
          body: ok(row),
          status: 201,
        }),
      );
    },
  },
  {
    method: 'PUT',
    path: /^\/api\/admin\/districts\/([^/]+)$/,
    handle: (req, m) => {
      const districtId = decodeURIComponent(m![1]!);
      const body = req.body as { name?: string } | null;
      const name = typeof body?.name === 'string' ? body.name.trim() : '';

      if (!name || name.length > 120) {
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'Validation.Failed', messageKey: 'app.validation' }], success: false },
            status: 400,
          }),
        );
      }

      for (const cityId of Object.keys(mockApiDistrictsByCity)) {
        const list = mockApiDistrictsByCity[cityId]!;

        const idx = list.findIndex((d) => d.id === districtId);

        if (idx >= 0) {
          const next = [...list];
          next[idx] = { cityId, id: districtId, name, slug: slugifyDistrictLabel(name) };

          mockApiDistrictsByCity[cityId] = next;

          return of(
            new HttpResponse({
              body: ok(next[idx]),
              status: 200,
            }),
          );
        }
      }

      return of(
        new HttpResponse({
          body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
          status: 404,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/admin\/properties$/,
    handle: (req) => {
      ensureMockApiPropertyStoreSeeded();
      const u = req.url.startsWith('http://') || req.url.startsWith('https://') ? new URL(req.url) : new URL(req.url, 'http://local');
      const q = u.searchParams;
      const page = Number.parseInt(q.get('page') ?? '1', 10) || 1;
      const pageSize = Number.parseInt(q.get('pageSize') ?? '20', 10) || 20;
      const items = [...mockApiProperties.values()].map((p) => {
        const c = cityOf(p.cityId);
        return {
          cityName: c?.name ?? '',
          districtName: districtName(p.cityId, p.districtId),
          id: p.id,
          isActive: p.isActive,
          primaryImageUrl: primaryUrl(p.images),
          propertyType: p.propertyType,
          publicSlug: p.publicSlug,
          referenceCode: p.referenceCode,
          state: p.state,
        };
      });
      const totalCount = items.length;
      const start = (page - 1) * pageSize;
      const slice = items.slice(start, start + pageSize);
      return of(
        new HttpResponse({
          body: ok({ items: slice, page, pageSize, totalCount }),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'GET',
    path: /^\/api\/admin\/properties\/([^/]+)$/,
    handle: (_req, m) => {
      ensureMockApiPropertyStoreSeeded();
      const id = decodeURIComponent(m![1]!);
      const p = mockApiProperties.get(id);

      if (p == null)
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );

      const sorted = [...p.images].sort((a, b) => {
        const o = a.sortOrder - b.sortOrder;
        return o !== 0 ? o : a.id.localeCompare(b.id, 'en');
      });
      const detail = {
        ...p,
        images: sorted.map((img) => ({
          adminOriginalUrl: img.adminOriginalUrl,
          externalId: img.externalId,
          id: img.id,
          isPrimary: img.isPrimary,
          sortOrder: img.sortOrder,
          storageProvider: img.storageProvider,
          url: img.url,
          urls: img.urls,
        })),
      };
      return of(
        new HttpResponse({
          body: ok(detail),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'PATCH',
    path: /^\/api\/admin\/properties\/([^/]+)\/listing-status$/,
    handle: (req, m) => {
      ensureMockApiPropertyStoreSeeded();
      const id = decodeURIComponent(m![1]!);
      const p = mockApiProperties.get(id);

      if (p == null)
        return of(
          new HttpResponse({
            body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false },
            status: 404,
          }),
        );

      const body = req.body as { listingStatus?: string } | null;
      const active = body?.listingStatus === 'active';
      p.isActive = active;
      p.updatedAtUtc = new Date().toISOString();
      return of(
        new HttpResponse({
          body: ok({
            id: p.id,
            isActive: p.isActive,
            referenceCode: p.referenceCode,
            updatedAtUtc: p.updatedAtUtc,
          }),
          status: 200,
        }),
      );
    },
  },
  {
    method: 'POST',
    path: /^\/api\/admin\/properties$/,
    handle: (req) =>
      from(
        (async (): Promise<HttpResponse<unknown>> => {
          ensureMockApiPropertyStoreSeeded();

          if (req.body instanceof FormData) {
            const parsed = await parseMultipart(req.body);

            if (!parsed.ok)
              return envelopeFail(400, parsed.messageKey);

            const pr = parsed.property;
            const id = globalThis.crypto.randomUUID();
            const city = cityOf(String(pr['cityId'] ?? ''));

            if (city == null)
              return envelopeFail(400, 'app.common.errors.unexpected');
            const imagesResult = applyImagePlan([], parsed.imagePlan, parsed.files, 'create');

            if (imagesResult instanceof HttpResponse)
              return imagesResult;

            const row: MockPropertyRecord = {
              bathroomCount: (pr['bathroomCount'] as null | number | undefined) ?? null,
              bedroomCount: (pr['bedroomCount'] as null | number | undefined) ?? null,
              cityId: String(pr['cityId'] ?? ''),
              complement: (pr['complement'] as null | string | undefined) ?? null,
              description: String(pr['description'] ?? ''),
              districtId: String(pr['districtId'] ?? ''),
              id,
              images: imagesResult,
              isActive: true,
              parkingSpaceCount: (pr['parkingSpaceCount'] as null | number | undefined) ?? null,
              postalCode: String(pr['postalCode'] ?? '').replace(/\D/g, ''),
              propertyType: Number(pr['propertyType'] ?? 1),
              publicSlug: `imo-${id.slice(0, 8)}`,
              referenceCode: refCode(),
              salePrice: Number(pr['salePrice'] ?? 0),
              state: city.state,
              streetLine: String(pr['streetLine'] ?? ''),
              streetNumber: String(pr['streetNumber'] ?? ''),
              title: (pr['title'] as null | string | undefined) ?? null,
              totalAreaSqm: (pr['totalAreaSqm'] as null | number | undefined) ?? null,
              updatedAtUtc: new Date().toISOString(),
            };
            mockApiProperties.set(id, row);
            return new HttpResponse({
              body: ok({
                id: row.id,
                isActive: row.isActive,
                referenceCode: row.referenceCode,
                updatedAtUtc: row.updatedAtUtc,
              }),
              status: 201,
            });
          }

          const pr = req.body as Record<string, unknown>;
          const id = globalThis.crypto.randomUUID();
          const city = cityOf(String(pr['cityId'] ?? ''));

          if (city == null)
            return envelopeFail(400, 'app.common.errors.unexpected');

          const row: MockPropertyRecord = {
            bathroomCount: (pr['bathroomCount'] as null | number | undefined) ?? null,
            bedroomCount: (pr['bedroomCount'] as null | number | undefined) ?? null,
            cityId: String(pr['cityId'] ?? ''),
            complement: (pr['complement'] as null | string | undefined) ?? null,
            description: String(pr['description'] ?? ''),
            districtId: String(pr['districtId'] ?? ''),
            id,
            images: [],
            isActive: true,
            parkingSpaceCount: (pr['parkingSpaceCount'] as null | number | undefined) ?? null,
            postalCode: String(pr['postalCode'] ?? '').replace(/\D/g, ''),
            propertyType: Number(pr['propertyType'] ?? 1),
            publicSlug: `imo-${id.slice(0, 8)}`,
            referenceCode: refCode(),
            salePrice: Number(pr['salePrice'] ?? 0),
            state: city.state,
            streetLine: String(pr['streetLine'] ?? ''),
            streetNumber: String(pr['streetNumber'] ?? ''),
            title: (pr['title'] as null | string | undefined) ?? null,
            totalAreaSqm: (pr['totalAreaSqm'] as null | number | undefined) ?? null,
            updatedAtUtc: new Date().toISOString(),
          };
          mockApiProperties.set(id, row);
          return new HttpResponse({
            body: ok({
              id: row.id,
              isActive: row.isActive,
              referenceCode: row.referenceCode,
              updatedAtUtc: row.updatedAtUtc,
            }),
            status: 201,
          });
        })(),
      ),
  },
  {
    method: 'PUT',
    path: /^\/api\/admin\/properties\/([^/]+)$/,
    handle: (req, m) =>
      from(
        (async (): Promise<HttpResponse<unknown>> => {
          ensureMockApiPropertyStoreSeeded();
          const id = decodeURIComponent(m![1]!);
          const prev = mockApiProperties.get(id);

          if (prev == null)
            return new HttpResponse({ body: { data: null, errors: [{ code: 'NotFound', messageKey: 'app.common.errors.unexpected' }], success: false }, status: 404 });

          if (req.body instanceof FormData) {
            const parsed = await parseMultipart(req.body);

            if (!parsed.ok)
              return envelopeFail(400, parsed.messageKey);

            const pr = parsed.property;
            const imagesResult = applyImagePlan(prev.images, parsed.imagePlan, parsed.files, 'edit');

            if (imagesResult instanceof HttpResponse)
              return imagesResult;

            const city = cityOf(String(pr['cityId'] ?? prev.cityId));

            if (city == null)
              return envelopeFail(400, 'app.common.errors.unexpected');
            const next: MockPropertyRecord = {
              bathroomCount: (pr['bathroomCount'] as null | number | undefined) ?? prev.bathroomCount,
              bedroomCount: (pr['bedroomCount'] as null | number | undefined) ?? prev.bedroomCount,
              cityId: String(pr['cityId'] ?? prev.cityId),
              complement: (pr['complement'] as null | string | undefined) ?? prev.complement,
              description: String(pr['description'] ?? prev.description),
              districtId: String(pr['districtId'] ?? prev.districtId),
              id: prev.id,
              images: imagesResult,
              isActive: prev.isActive,
              parkingSpaceCount: (pr['parkingSpaceCount'] as null | number | undefined) ?? prev.parkingSpaceCount,
              postalCode: String(pr['postalCode'] ?? prev.postalCode).replace(/\D/g, ''),
              propertyType: Number(pr['propertyType'] ?? prev.propertyType),
              publicSlug: prev.publicSlug,
              referenceCode: prev.referenceCode,
              salePrice: Number(pr['salePrice'] ?? prev.salePrice),
              state: city.state,
              streetLine: String(pr['streetLine'] ?? prev.streetLine),
              streetNumber: String(pr['streetNumber'] ?? prev.streetNumber),
              title: (pr['title'] as null | string | undefined) ?? prev.title,
              totalAreaSqm: (pr['totalAreaSqm'] as null | number | undefined) ?? prev.totalAreaSqm,
              updatedAtUtc: new Date().toISOString(),
            };
            mockApiProperties.set(id, next);
            return new HttpResponse({
              body: ok({
                id: next.id,
                isActive: next.isActive,
                referenceCode: next.referenceCode,
                updatedAtUtc: next.updatedAtUtc,
              }),
              status: 200,
            });
          }
          const pr = req.body as Record<string, unknown>;
          const city = cityOf(String(pr['cityId'] ?? prev.cityId));

          if (city == null)
            return envelopeFail(400, 'app.common.errors.unexpected');

          const next: MockPropertyRecord = {
            ...prev,
            bathroomCount: (pr['bathroomCount'] as null | number | undefined) ?? prev.bathroomCount,
            bedroomCount: (pr['bedroomCount'] as null | number | undefined) ?? prev.bedroomCount,
            cityId: String(pr['cityId'] ?? prev.cityId),
            complement: (pr['complement'] as null | string | undefined) ?? prev.complement,
            description: String(pr['description'] ?? prev.description),
            districtId: String(pr['districtId'] ?? prev.districtId),
            parkingSpaceCount: (pr['parkingSpaceCount'] as null | number | undefined) ?? prev.parkingSpaceCount,
            postalCode: String(pr['postalCode'] ?? prev.postalCode).replace(/\D/g, ''),
            propertyType: Number(pr['propertyType'] ?? prev.propertyType),
            salePrice: Number(pr['salePrice'] ?? prev.salePrice),
            state: city.state,
            streetLine: String(pr['streetLine'] ?? prev.streetLine),
            streetNumber: String(pr['streetNumber'] ?? prev.streetNumber),
            title: (pr['title'] as null | string | undefined) ?? prev.title,
            totalAreaSqm: (pr['totalAreaSqm'] as null | number | undefined) ?? prev.totalAreaSqm,
            updatedAtUtc: new Date().toISOString(),
          };
          mockApiProperties.set(id, next);
          return new HttpResponse({
            body: ok({
              id: next.id,
              isActive: next.isActive,
              referenceCode: next.referenceCode,
              updatedAtUtc: next.updatedAtUtc,
            }),
            status: 200,
          });
        })(),
      ),
  },
];
